/**
 * entryLoaderContent.ts — what the journey entry loader's content card says
 * (CTO, 2026-09-25).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (the entry loader's content card).
 *
 * THE ASKS, verbatim. Afternoon: "show a bit more information: it's a
 * quick-to-fill form; how the process works; … there is no charge till your
 * treatment is approved, and then you can request a [call back] if needed. Tell
 * them about the switch and save a bit, and also tell them how we can support
 * [them]. Use that loading page as a bit of a small content page, not too much,
 * but then they have enough material to read till it loads, because on staging,
 * it currently takes a good 4 to 5 seconds to load."
 * Evening, on the first cut's rotating panels: "keep switch and save in …
 * Especially in this loader, [I] don't want to have that carousel side of
 * things, and then [the] ability to pause. It should nicely load one after the
 * other, showing the journey till the main page loads."
 * ~18:00, on the numbered list that replaced it: "Yes, you can make the card
 * grow as the items appear, but I think right now it's too much text. Let's try
 * to use more icons if possible … You need to clearly explain the message, but
 * use very catchy text … and then try to grab the attention because we have a
 * very short window here. We need to get the maximum out of it."
 *
 * ── THE SHAPE: ICONS AND HEADLINES, BUILT UP ONE AFTER ANOTHER ──────────────
 * The same content (every item of it — the CTO said keep Switch & Save), cut to
 * roughly half the words and led by icons (`ui/LoaderJourneyCard.tsx`):
 *   1–4. THE JOURNEY, in the order it runs — a stopwatch "2-minute check", a
 *        card "No charge until approved", a stethoscope "UK clinician review",
 *        a parcel "Approved? Sent to your door" — each with one short line.
 *   5–7. THREE CHIPS under a hairline — the free call back, Switch & Save (its
 *        figure, qualifier and comparator run in), and our team.
 * Seven items, the first with the card and one every `REVEAL_STEP_MS` after:
 * the whole card is on screen {@link ENTRY_LOADER_REVEALED_BY_MS} after mount
 * (show delay included) — so a 4–5s stage load shows all of it — and then it is
 * static. `entryLoaderContent.spec` pins that on a fake clock.
 *
 * ── SHORT AND CATCHY, BUT NEVER A NEW CLAIM (DMCC Act 2024 / CAP Code 3.1,
 * 3.7 and s.13; R28) ─────────────────────────────────────────────────────────
 * Every line restates a claim the site ALREADY makes, and says no more than it:
 * a constant another screen prints, imported by name, or a SHORT FORM that
 * lives beside the long claim it restates (`serviceClaims.ts`,
 * `switchAndSaveCopy.ts`) or here with its source named on it. The table
 * (CHANGELOG, 2026-09-25 "icon-led") maps every line to its source file and
 * line; `entryLoaderContent.spec` pins each short form against its source, so
 * a reword of the source turns it red. What the card may never carry: a rating,
 * a statistic, a speed beyond "usually within one working day", "£0 today" or
 * "free" (a pre-authorisation HOLD is placed — the claim is "no charge until
 * approved"), an outcome or weight promise, a medicine or delivery-form word,
 * "discreet" beyond the Result screen's own "Discreet delivery", and — the Art 9
 * list — "switching" (the obvious hook for Switch & Save, and treatment-status
 * phrasing on `HEALTH_SHAPED`).
 *
 *   step 1.  {@link TWO_MINUTE_CHECK} (verbatim) · {@link A_FEW_HEALTH_QUESTIONS}
 *   step 2.  `NO_CHARGE_UNTIL_APPROVED_SHORT` · {@link CHOOSE_PLAN_ADD_CARD}
 *   step 3.  `CLINICIAN_REVIEW_SHORT` · `REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY`
 *   step 4.  {@link APPROVED_SENT_TO_YOUR_DOOR} · `DISCREET_DELIVERY` (verbatim)
 *   call back.     {@link FREE_CALL_BACK}
 *   Switch & Save. `SWITCH_SAVE_NAME` — `SWITCH_SAVE_SHORT` (the 8–10% is
 *                  `SWITCH_SAVE_RANGE`, never retyped)
 *   our team.      `TEAM_SUPPORT.title` (verbatim)
 * The only words minted here that restate nothing are the heading ("Here's how
 * it works" — it names the list under it).
 *
 * ── PUBLIC, PRE-ELIGIBILITY ─────────────────────────────────────────────────
 * The card shows before any eligibility decision, so: category-level only
 * (R28 — no medicine, brand, molecule or delivery-form word), nothing about the
 * visitor's body (the Art 9 `HEALTH_SHAPED` list), no price figure. Swept by
 * `entryLoaderContent.spec.tsx` and `r28.spec.tsx`. And only on a FRESH START
 * (`journeyLoaderCard`): the self-serve lane, which always takes the hold
 * route, so "no charge until approved" is true for everyone who can see it.
 *
 * ⚠️ THE SWITCH & SAVE CHIP PUTS A COMPARATIVE PRICE CLAIM ON A PUBLIC SURFACE.
 * Until 2026-09-25 the 8–10% sat only beside the prior-treatment question, which
 * clinic-service serves after the eligibility gate (that QUESTION's options
 * name prescription-only medicines — the reason it is post-gate). The saving
 * words name no medicine and pass R28, and the CTO asked for them here (and,
 * twice, to keep them). What they still lack is the CAP Code 3.7
 * substantiation: no dated basket of competitors' published prices is held
 * (prices #65 reverted by #66), so the claim is exposed wherever it runs — and
 * this placement is the widest yet. OPEN CTO ITEM (CLAUDE.md, Switch & Save).
 * Pulling it is one line: drop `SWITCH_SAVE_CHIP` from {@link ENTRY_LOADER_CHIPS}.
 */
import { TEAM_SUPPORT } from '../checkout/celebrationCopy';
import { LOADER_SHOW_DELAY_MS } from '../ui/BrandedLoader';
import { revealDoneMs, type LoaderJourney, type LoaderJourneyChip, type LoaderJourneyStep } from '../ui/LoaderJourneyCard';
import { DISCREET_DELIVERY } from './resultCopy';
import {
  CLINICIAN_REVIEW_SHORT,
  NO_CHARGE_UNTIL_APPROVED_SHORT,
  REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY,
  TWO_MINUTE_CHECK,
} from './serviceClaims';
import { SWITCH_SAVE_NAME, SWITCH_SAVE_SHORT } from './switchAndSaveCopy';

/** The card's heading (and accessible name): it names the journey under it. The one line that restates nothing. */
export const ENTRY_LOADER_HEADING = 'Here’s how it works';

/**
 * Step 1's line. SOURCE: the first "What happens next" step's title, "Answer
 * a few health questions" (`WhatHappensNext.tsx`, `WHAT_HAPPENS_NEXT_STEPS[0]`)
 * — the same words without the "Answer".
 */
export const A_FEW_HEALTH_QUESTIONS = 'A few health questions';

/**
 * Step 2's line. SOURCE: the second "What happens next" step's title, "Choose
 * a plan and add a card" (`WhatHappensNext.tsx`, `WHAT_HAPPENS_NEXT_STEPS[1]`)
 * — the same two actions, with the "and" dropped.
 */
export const CHOOSE_PLAN_ADD_CARD = 'Choose a plan, add a card';

/**
 * Step 4's headline. SOURCE, word by word: the celebration's hold trust point,
 * "Once approved, your treatment is sent to your door in plain packaging."
 * (`checkout/celebrationCopy.ts`, `celebrationCopy('hold').trust[1].body`) —
 * the same words in the same order with some dropped, and none added
 * (`entryLoaderContent.spec` pins it). The Result screen's delivery step says
 * it too ("Sent to your door in plain, insulated packaging.", `ResultScreen.tsx`),
 * and the fourth "What happens next" title makes the same condition ("If
 * approved, we send your treatment", `WhatHappensNext.tsx`).
 *
 * WHY NOT "IT'S ON ITS WAY" (review, 2026-09-25). The sources are conditional
 * and future ("once approved … is sent"); a present-tense "it's on its way"
 * reads as a parcel already in transit the moment it is approved, skipping the
 * payment and dispatch the sources name — and it was the one card line no
 * reword of its source would have turned red. Nothing about WHEN it arrives (no
 * delivery time is claimed anywhere on the site, so none is here). The payment
 * half is step 2's "No charge until approved".
 */
export const APPROVED_SENT_TO_YOUR_DOOR = 'Approved? Sent to your door';

/**
 * The call-back chip. SOURCE: the call-back offer's own action, "Request a free
 * call back" (`JourneyCallbackOffer.tsx`, `CALLBACK_OFFER_ACTION`), offered on
 * every patient page by the header's "Speak to a clinician now" CTA.
 */
export const FREE_CALL_BACK = 'Free call back';

/** The journey, in the order it runs. */
export const ENTRY_LOADER_STEPS: ReadonlyArray<LoaderJourneyStep> = [
  { id: 'questions', icon: 'stopwatch', title: TWO_MINUTE_CHECK, detail: A_FEW_HEALTH_QUESTIONS },
  { id: 'plan', icon: 'card', title: NO_CHARGE_UNTIL_APPROVED_SHORT, detail: CHOOSE_PLAN_ADD_CARD },
  { id: 'review', icon: 'clinician', title: CLINICIAN_REVIEW_SHORT, detail: REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY },
  { id: 'dispatch', icon: 'parcel', title: APPROVED_SENT_TO_YOUR_DOOR, detail: DISCREET_DELIVERY },
];

/** A free call back, if they would rather talk. */
export const CALLBACK_CHIP: LoaderJourneyChip = { id: 'call-back', icon: 'call', title: FREE_CALL_BACK };

/** Switch & Save — see the ⚠️ in the header. The name, then its short form run in. */
export const SWITCH_SAVE_CHIP: LoaderJourneyChip = {
  id: 'switch-save',
  icon: 'save',
  title: SWITCH_SAVE_NAME,
  detail: SWITCH_SAVE_SHORT,
};

/** How our team supports them — the celebration's team point's own title. */
export const TEAM_SUPPORT_CHIP: LoaderJourneyChip = { id: 'team-support', icon: 'team', title: TEAM_SUPPORT.title };

/** The chips, revealed after the journey, in the order the CTO listed them. */
export const ENTRY_LOADER_CHIPS: ReadonlyArray<LoaderJourneyChip> = [CALLBACK_CHIP, SWITCH_SAVE_CHIP, TEAM_SUPPORT_CHIP];

/**
 * The whole card, as `BrandedLoader`'s journey look takes it. A module
 * constant, so a re-render never hands the card a new object.
 */
export const ENTRY_LOADER_CARD: LoaderJourney = {
  heading: ENTRY_LOADER_HEADING,
  steps: ENTRY_LOADER_STEPS,
  chips: ENTRY_LOADER_CHIPS,
};

/**
 * WHEN THE WHOLE CARD IS ON SCREEN, from the loader's MOUNT: the 300ms show
 * delay (the card is drawn with the mark), then the reveal — the first item
 * with the card, one every `REVEAL_STEP_MS`, the last one fully grown in.
 * 300 + 6 × 350 + 450 = 2850ms (2550ms after the card appears): a typical
 * 4–5s stage entry shows every item, and the card is static from then on
 * (WCAG 2.2.2 — see `ui/LoaderJourneyCard.tsx`). `entryLoaderContent.spec`
 * pins it ≤ 3.3s from mount (≤ 3s after the card) and walks the real loader on
 * a fake clock to prove it.
 */
export const ENTRY_LOADER_REVEALED_BY_MS =
  LOADER_SHOW_DELAY_MS + revealDoneMs(ENTRY_LOADER_STEPS.length + ENTRY_LOADER_CHIPS.length);
